import type { StaffInfo } from "../lib/api.ts";

interface StaffPickerProps {
  staff: StaffInfo[];
  selected: string | null;
  onSelect: (staffId: string | null) => void;
}

interface ChipProps {
  label: string;
  initial: string;
  avatarUrl?: string | null;
  selected: boolean;
  onClick: () => void;
}

function Chip({ label, initial, avatarUrl, selected, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 flex flex-col items-center gap-1.5 px-3 py-2 rounded-[var(--radius)] border-2 transition-all duration-150 min-w-[64px] ${
        selected
          ? "border-[var(--accent)] bg-[var(--accent)]/10"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      {/* Avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={label}
          className="w-10 h-10 rounded-full object-cover"
        />
      ) : (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
          style={{ backgroundColor: selected ? "var(--accent)" : "#9ca3af" }}
        >
          {initial}
        </div>
      )}
      <span
        className={`text-xs font-medium leading-tight text-center max-w-[72px] truncate ${
          selected ? "text-[var(--accent)]" : "text-gray-700"
        }`}
      >
        {label}
      </span>
    </button>
  );
}

export function StaffPicker({ staff, selected, onSelect }: StaffPickerProps) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
      {/* "Any available" option */}
      <Chip
        label="Any available"
        initial="?"
        selected={selected === null}
        onClick={() => onSelect(null)}
      />

      {staff.map((member) => (
        <Chip
          key={member.id}
          label={member.name}
          initial={member.name.charAt(0).toUpperCase()}
          avatarUrl={member.avatar_url}
          selected={selected === member.id}
          onClick={() => onSelect(member.id)}
        />
      ))}
    </div>
  );
}
